import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ApiClient } from '../../api/client';
import { formatFileSize, getFileIcon } from './AttachmentManagerModal';
import { WebpageSaveDialog } from './WebpageSaveDialog';
import {
  Paperclip, X, Search, FileText, FileSpreadsheet, FileImage,
  AlertTriangle, CheckCircle2, ArrowRight, RefreshCw,
  Download, Eye, FolderOpen, Layers, ExternalLink, RotateCcw, XCircle, Globe, Trash2
} from 'lucide-react';

export interface AttachmentOverviewFile {
  id: number;
  filename: string;
  ext: string;
  size: number;
  mime_type: string;
  uploaded_at: string;
  type_group: string;
  is_warning: boolean;
  record_id: number;
  record_pont: string;
  record_title: string;
  page_id: number;
  page_title: string;
  page_slug: string;
  page_icon: string;
  url: string;
  source_url?: string | null;
}

/** A weboldal-átemelés során le nem töltött melléklet (séma v59). */
export interface AttachmentFailure {
  id: number;
  label: string;
  filename: string;
  source_url: string;
  reason: string;
  attempts: number;
  last_attempt_at: string;
  record_id: number;
  record_pont: string;
  record_title: string;
  page_id: number;
  page_title: string;
  page_slug: string;
  page_icon: string;
}

// csak http(s) cím lehet kattintható hivatkozás
const safeHttpUrl = (u?: string | null) => (u && /^https?:\/\//i.test(u) ? u : null);

interface AttachmentStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId?: number;
  tenantSlug?: string;
  activePageId?: number;
  activePageTitle?: string;
  onNavigateToRecord: (pageId: number, recordId: number) => void;
  onOpenRecordAttachments: (recordId: number, pageId: number) => void;
}

export const AttachmentStatsModal: React.FC<AttachmentStatsModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  tenantSlug: _tenantSlug = 'system',
  activePageId,
  activePageTitle,
  onNavigateToRecord,
  onOpenRecordAttachments,
}) => {
  const [data, setData] = useState<{
    total_count: number;
    total_size: number;
    warning_count: number;
    types: Record<string, { label: string; count: number; size: number; ext: string; color: string }>;
    files: AttachmentOverviewFile[];
    failures?: AttachmentFailure[];
    failed_count?: number;
  } | null>(null);

  // ── Újrapróbálás (le nem töltött / HTML-ként érkezett fájlok) ──
  // kulcs: 'f:<id>' = le nem töltött tétel, 'a:<id>' = meglévő melléklet újra letöltése
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [retryRun, setRetryRun] = useState<{ done: number; total: number; ok: number; current: string } | null>(null);
  const [retryErrors, setRetryErrors] = useState<Record<string, string>>({});
  const [retryingKey, setRetryingKey] = useState<string | null>(null);
  // „Mentés weboldalként” ablak (ha fájl helyett weboldal érkezett)
  const [webpageFor, setWebpageFor] = useState<AttachmentFailure | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string>('all');
  const [scopeFilter, setScopeFilter] = useState<'all' | 'page'>('all');
  const [replacingId, setReplacingId] = useState<number | null>(null);
  const [isReplacing, setIsReplacing] = useState(false);
  const [successToast, setSuccessToast] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const targetReplaceAttIdRef = useRef<number | null>(null);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const pageFilter = scopeFilter === 'page' && activePageId ? activePageId : undefined;
      const res = await ApiClient.getAttachmentsOverview(tenantId, pageFilter);
      if (res.ok && res.data) {
        setData(res.data);
      } else {
        setError(res.error || 'Nem sikerült betölteni a melléklet-statisztikát.');
      }
    } catch (e: any) {
      setError(e.message || 'Hiba történt a statisztika lekérése során.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, scopeFilter, activePageId, tenantId]);

  // Fájlcsere kezelése közvetlenül innen
  const handleTriggerReplace = (attId: number) => {
    targetReplaceAttIdRef.current = attId;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const attId = targetReplaceAttIdRef.current;
    if (!file || !attId) return;

    setIsReplacing(true);
    setReplacingId(attId);
    try {
      const res = await ApiClient.replaceAttachment(attId, file);
      if (res.ok) {
        setSuccessToast(`A fájl sikeresen lecserélve erre: „${file.name}”!`);
        setTimeout(() => setSuccessToast(null), 4000);
        await loadData();
      } else {
        alert(res.error || 'Hiba történt a fájl cseréje során.');
      }
    } catch (err: any) {
      alert('Hiba: ' + (err.message || 'Hálózati hiba'));
    } finally {
      setIsReplacing(false);
      setReplacingId(null);
      targetReplaceAttIdRef.current = null;
    }
  };

  // Szűrt fájllista
  const filteredFiles = useMemo(() => {
    if (!data?.files) return [];
    let list = data.files;

    if (selectedTypeFilter !== 'all') {
      if (selectedTypeFilter === 'warning') {
        list = list.filter(f => f.is_warning);
      } else {
        list = list.filter(f => f.type_group === selectedTypeFilter);
      }
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(f =>
        f.filename.toLowerCase().includes(q) ||
        f.record_pont.toLowerCase().includes(q) ||
        f.record_title.toLowerCase().includes(q) ||
        f.page_title.toLowerCase().includes(q)
      );
    }

    return list;
  }, [data?.files, selectedTypeFilter, searchQuery]);

  const failures = data?.failures || [];
  const filteredFailures = useMemo(() => {
    if (!['all', 'html', 'warning'].includes(selectedTypeFilter)) return [];
    const q = searchQuery.toLowerCase().trim();
    if (!q) return failures;
    return failures.filter(f =>
      (f.filename || '').toLowerCase().includes(q) ||
      (f.label || '').toLowerCase().includes(q) ||
      (f.record_pont || '').toLowerCase().includes(q) ||
      (f.record_title || '').toLowerCase().includes(q) ||
      (f.page_title || '').toLowerCase().includes(q) ||
      (f.reason || '').toLowerCase().includes(q)
    );
  }, [failures, selectedTypeFilter, searchQuery]);

  // a képernyőn látható, újrapróbálható tételek (le nem töltött + HTML-ként érkezett, forráscímmel)
  const retryableKeys = useMemo(() => [
    ...filteredFailures.map(f => `f:${f.id}`),
    ...filteredFiles.filter(f => f.is_warning && safeHttpUrl(f.source_url)).map(f => `a:${f.id}`),
  ], [filteredFailures, filteredFiles]);
  const selectedVisible = retryableKeys.filter(k => selectedKeys.has(k));
  const allVisibleSelected = retryableKeys.length > 0 && selectedVisible.length === retryableKeys.length;

  const toggleKey = (k: string) => setSelectedKeys(prev => {
    const n = new Set(prev);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  const toggleAllVisible = () => setSelectedKeys(prev => {
    const n = new Set(prev);
    if (allVisibleSelected) retryableKeys.forEach(k => n.delete(k)); else retryableKeys.forEach(k => n.add(k));
    return n;
  });

  const retryOne = async (key: string) => {
    const [kind, idStr] = key.split(':');
    const id = Number(idStr);
    const res: any = kind === 'f' ? await ApiClient.retryAttachmentFailure(id) : await ApiClient.refetchAttachment(id);
    return res;
  };

  /** Tétel elvetése (nem kell / kézzel pótolva). */
  const dismissFailure = async (f: AttachmentFailure) => {
    if (!window.confirm(`Leveszed a listáról: „${f.filename || f.label}”? (A forrásoldalon lévő fájl nem kerül a tételhez.)`)) return;
    const r: any = await ApiClient.resolveAttachmentFailure(f.id);
    if (!r?.ok) { setError(r?.error || 'Nem sikerült levenni a listáról'); return; }
    setSelectedKeys(prev => { const n = new Set(prev); n.delete(`f:${f.id}`); return n; });
    setSuccessToast(`Levéve a listáról: „${f.filename || f.label}”.`);
    setTimeout(() => setSuccessToast(null), 4000);
    await loadData();
  };
  const isWebpageFailure = (f: AttachmentFailure) => /weboldal érkezett/i.test(retryErrors[`f:${f.id}`] || f.reason || '');

  /** Egy vagy több tétel újrapróbálása – egymás után, folyamatjelzővel. */
  const runRetry = async (keys: string[]) => {
    if (keys.length === 0 || retryRun) return;
    const nameOf = (k: string) => {
      const [kind, idStr] = k.split(':');
      const id = Number(idStr);
      return kind === 'f'
        ? (failures.find(f => f.id === id)?.filename || failures.find(f => f.id === id)?.label || `#${id}`)
        : (data?.files.find(f => f.id === id)?.filename || `#${id}`);
    };
    let ok = 0;
    const errs: Record<string, string> = {};
    setRetryErrors(prev => { const n = { ...prev }; keys.forEach(k => delete n[k]); return n; });
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      setRetryRun({ done: i, total: keys.length, ok, current: nameOf(k) });
      setRetryingKey(k);
      try {
        const r = await retryOne(k);
        if (r?.ok) ok++; else errs[k] = r?.error || 'ismeretlen hiba';
      } catch (e: any) {
        errs[k] = e?.message || 'hálózati hiba';
      }
    }
    setRetryingKey(null);
    setRetryRun(null);
    setRetryErrors(prev => ({ ...prev, ...errs }));
    setSelectedKeys(prev => { const n = new Set(prev); keys.forEach(k => { if (!errs[k]) n.delete(k); }); return n; });
    const failed = keys.length - ok;
    setSuccessToast(
      keys.length === 1
        ? (ok ? `Sikerült letölteni: „${nameOf(keys[0])}”.` : `Most sem sikerült: ${errs[keys[0]]}`)
        : `Újrapróbálás kész: ${ok} sikerült${failed ? `, ${failed} most sem (az ok a tételnél látszik)` : ''}.`
    );
    setTimeout(() => setSuccessToast(null), 6000);
    await loadData();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden">
        
        {/* Rejtett fájlbemenet a közvetlen cseréhez */}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
        />

        {/* Modal Fejléc */}
        <div className="p-4 sm:p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between gap-4 bg-slate-50/50 dark:bg-slate-900/50">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2.5 rounded-xl bg-blue-100 dark:bg-blue-950/70 text-blue-600 dark:text-cyan-400 shrink-0">
              <Paperclip className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2 flex-wrap">
                <span>Csatolt állományok statisztikája & Pont-térkép</span>
                {data && (
                  <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-cyan-300 border border-blue-200 dark:border-blue-800 font-mono">
                    {data.total_count} db ({formatFileSize(data.total_size)})
                  </span>
                )}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                Fájltípusok megoszlása, letöltési vizsgálat és közvetlen ugrás a közzétételi pontokhoz
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={loadData}
              disabled={isLoading}
              className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition"
              title="Adatok frissítése"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition"
              title="Bezárás"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Sikeres művelet toast üzenet */}
        {successToast && (
          <div className="px-4 py-2.5 bg-emerald-50 dark:bg-emerald-950/70 border-b border-emerald-200 dark:border-emerald-800/60 text-emerald-800 dark:text-emerald-200 text-xs font-semibold flex items-center justify-between gap-2 animate-in slide-in-from-top-1">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span>{successToast}</span>
            </div>
            <button onClick={() => setSuccessToast(null)} className="text-emerald-600 hover:text-emerald-800">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Hibaüzenet banner */}
        {error && (
          <div className="px-4 py-2.5 bg-red-50 dark:bg-red-950/70 border-b border-red-200 dark:border-red-800/60 text-red-800 dark:text-red-200 text-xs font-semibold flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0" />
              <span>{error}</span>
            </div>
            <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Modal Törzs */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          
          {/* Hatókör és Típus statisztika kártyák */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {/* Hatókör választó */}
            <div className="flex items-center gap-1.5 p-1 rounded-xl bg-slate-100 dark:bg-slate-800/80 w-fit text-xs font-medium">
              <button
                onClick={() => setScopeFilter('all')}
                className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${
                  scopeFilter === 'all'
                    ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white font-bold shadow-xs'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                }`}
              >
                Minden oldal ({data?.total_count || 0})
              </button>
              {activePageTitle && (
                <button
                  onClick={() => setScopeFilter('page')}
                  className={`px-3 py-1.5 rounded-lg transition cursor-pointer max-w-[200px] truncate ${
                    scopeFilter === 'page'
                      ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white font-bold shadow-xs'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                  }`}
                  title={activePageTitle}
                >
                  Csak: {activePageTitle}
                </button>
              )}
            </div>

            {failures.length > 0 && (
              <div
                onClick={() => setSelectedTypeFilter('html')}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-rose-50 dark:bg-rose-950/60 border border-rose-300 dark:border-rose-800 text-rose-800 dark:text-rose-300 text-xs font-bold cursor-pointer hover:bg-rose-100 transition shadow-xs"
              >
                <XCircle className="w-4 h-4 text-rose-600 shrink-0" />
                <span>{failures.length} db fájl nem töltődött le a forrásoldalról</span>
                <span className="text-[10px] underline ml-1">Megnézem</span>
              </div>
            )}

            {/* Figyelmeztetés jelvény, ha van hibás/HTML fájl */}
            {(data?.warning_count || 0) > 0 && (
              <div
                onClick={() => setSelectedTypeFilter(selectedTypeFilter === 'warning' ? 'all' : 'warning')}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-amber-50 dark:bg-amber-950/70 border border-amber-300 dark:border-amber-700/80 text-amber-800 dark:text-amber-300 text-xs font-bold cursor-pointer hover:bg-amber-100 transition shadow-xs"
              >
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <span>
                  {data?.warning_count} db gyanús (HTML / bejelentkező) fájl cserére szorulhat!
                </span>
                <span className="text-[10px] underline ml-1">Szűrés</span>
              </div>
            )}
          </div>

          {/* Típus statisztika grid */}
          {data?.types && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-2.5">
              {/* Összes gomb */}
              <button
                onClick={() => setSelectedTypeFilter('all')}
                className={`p-3 rounded-xl border text-left transition cursor-pointer flex flex-col justify-between ${
                  selectedTypeFilter === 'all'
                    ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 border-slate-900 dark:border-white shadow-sm'
                    : 'bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-800 hover:border-slate-400'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase font-bold tracking-wider opacity-75">Összes</span>
                  <Layers className="w-3.5 h-3.5 opacity-75" />
                </div>
                <div className="mt-1">
                  <span className="text-lg font-extrabold">{data.total_count}</span>
                  <div className="text-[10px] opacity-75 truncate">{formatFileSize(data.total_size)}</div>
                </div>
              </button>

              {/* PDF */}
              <button
                onClick={() => setSelectedTypeFilter(selectedTypeFilter === 'pdf' ? 'all' : 'pdf')}
                className={`p-3 rounded-xl border text-left transition cursor-pointer flex flex-col justify-between ${
                  selectedTypeFilter === 'pdf'
                    ? 'bg-red-600 text-white border-red-600 shadow-sm'
                    : 'bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-800 hover:border-red-400'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase font-bold tracking-wider opacity-85">PDF</span>
                  <FileText className={`w-3.5 h-3.5 ${selectedTypeFilter === 'pdf' ? 'text-white' : 'text-red-500'}`} />
                </div>
                <div className="mt-1">
                  <span className="text-lg font-extrabold">{data.types.pdf.count}</span>
                  <div className="text-[10px] opacity-75 truncate">{formatFileSize(data.types.pdf.size)}</div>
                </div>
              </button>

              {/* Word */}
              <button
                onClick={() => setSelectedTypeFilter(selectedTypeFilter === 'word' ? 'all' : 'word')}
                className={`p-3 rounded-xl border text-left transition cursor-pointer flex flex-col justify-between ${
                  selectedTypeFilter === 'word'
                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                    : 'bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-800 hover:border-blue-400'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase font-bold tracking-wider opacity-85">Word</span>
                  <FileText className={`w-3.5 h-3.5 ${selectedTypeFilter === 'word' ? 'text-white' : 'text-blue-500'}`} />
                </div>
                <div className="mt-1">
                  <span className="text-lg font-extrabold">{data.types.word.count}</span>
                  <div className="text-[10px] opacity-75 truncate">{formatFileSize(data.types.word.size)}</div>
                </div>
              </button>

              {/* Excel */}
              <button
                onClick={() => setSelectedTypeFilter(selectedTypeFilter === 'excel' ? 'all' : 'excel')}
                className={`p-3 rounded-xl border text-left transition cursor-pointer flex flex-col justify-between ${
                  selectedTypeFilter === 'excel'
                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                    : 'bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-800 hover:border-emerald-400'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase font-bold tracking-wider opacity-85">Excel / CSV</span>
                  <FileSpreadsheet className={`w-3.5 h-3.5 ${selectedTypeFilter === 'excel' ? 'text-white' : 'text-emerald-500'}`} />
                </div>
                <div className="mt-1">
                  <span className="text-lg font-extrabold">{data.types.excel.count}</span>
                  <div className="text-[10px] opacity-75 truncate">{formatFileSize(data.types.excel.size)}</div>
                </div>
              </button>

              {/* Kép */}
              <button
                onClick={() => setSelectedTypeFilter(selectedTypeFilter === 'image' ? 'all' : 'image')}
                className={`p-3 rounded-xl border text-left transition cursor-pointer flex flex-col justify-between ${
                  selectedTypeFilter === 'image'
                    ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                    : 'bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-800 hover:border-purple-400'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase font-bold tracking-wider opacity-85">Kép</span>
                  <FileImage className={`w-3.5 h-3.5 ${selectedTypeFilter === 'image' ? 'text-white' : 'text-purple-500'}`} />
                </div>
                <div className="mt-1">
                  <span className="text-lg font-extrabold">{data.types.image.count}</span>
                  <div className="text-[10px] opacity-75 truncate">{formatFileSize(data.types.image.size)}</div>
                </div>
              </button>

              {/* HTML / Hiba */}
              <button
                onClick={() => setSelectedTypeFilter(selectedTypeFilter === 'html' ? 'all' : 'html')}
                className={`p-3 rounded-xl border text-left transition cursor-pointer flex flex-col justify-between ${
                  selectedTypeFilter === 'html'
                    ? 'bg-amber-600 text-white border-amber-600 shadow-sm'
                    : data.types.html.count + failures.length > 0
                      ? 'bg-amber-50/60 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200 border-amber-300 dark:border-amber-800 hover:border-amber-400'
                      : 'bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-800'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase font-bold tracking-wider opacity-85">HTML / Hiba</span>
                  <AlertTriangle className={`w-3.5 h-3.5 ${selectedTypeFilter === 'html' ? 'text-white' : 'text-amber-500'}`} />
                </div>
                <div className="mt-1">
                  <span className="text-lg font-extrabold">{data.types.html.count + failures.length}</span>
                  <div className="text-[10px] opacity-75 truncate">
                    {failures.length > 0 ? `${failures.length} nem töltődött le` : formatFileSize(data.types.html.size)}
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* Kereső és Szűrő sáv */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
            <div className="relative w-full sm:max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Keresés fájlnév, pont száma vagy megnevezés alapján..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-8 py-2 rounded-xl text-xs bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="text-xs text-slate-500 dark:text-slate-400 self-end sm:self-center font-medium">
              Megjelenítve: <span className="font-bold text-slate-900 dark:text-white">{filteredFiles.length + filteredFailures.length}</span> / {(data?.total_count || 0) + failures.length} fájl
            </div>
          </div>

          {/* Újrapróbálás sáv: a képernyőn látható le nem töltött / HTML-ként érkezett fájlokhoz */}
          {retryableKeys.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-xl border border-amber-200 dark:border-amber-800/60 bg-amber-50/60 dark:bg-amber-950/20 text-xs">
              <label className="flex items-center gap-2 font-semibold text-amber-900 dark:text-amber-200 cursor-pointer">
                <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} disabled={!!retryRun} className="w-4 h-4 accent-amber-600" />
                <span>Mind kijelölése ({retryableKeys.length} újrapróbálható)</span>
              </label>
              <div className="flex items-center gap-2">
                {retryRun ? (
                  <span className="text-amber-800 dark:text-amber-300 font-medium" aria-live="polite">
                    Letöltés: {retryRun.done + 1} / {retryRun.total} – „{retryRun.current.slice(0, 40)}”…
                  </span>
                ) : (
                  <span className="text-slate-500 dark:text-slate-400">A letöltés időkorlátját a superadmin a Weboldal Átemelőben állítja.</span>
                )}
                <button
                  type="button"
                  onClick={() => runRetry(selectedVisible)}
                  disabled={selectedVisible.length === 0 || !!retryRun}
                  className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-semibold flex items-center gap-1.5 cursor-pointer"
                >
                  <RotateCcw className={`w-3.5 h-3.5 ${retryRun ? 'animate-spin' : ''}`} />
                  Kijelöltek újrapróbálása ({selectedVisible.length})
                </button>
              </div>
            </div>
          )}

          {/* Fájlok tételes listája */}
          <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden divide-y divide-slate-100 dark:divide-slate-800/70 bg-white dark:bg-slate-900">
            {isLoading ? (
              <div className="p-12 text-center text-slate-400 text-xs flex flex-col items-center justify-center gap-2">
                <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
                <span>Mellékletek betöltése és fájlelemzés folyamatban...</span>
              </div>
            ) : filteredFiles.length === 0 && filteredFailures.length === 0 ? (
              <div className="p-12 text-center text-slate-400 text-xs">
                Nem található a szűrésnek megfelelő csatolt állomány.
              </div>
            ) : (
              <>
              {filteredFailures.map(f => {
                const key = `f:${f.id}`;
                const src = safeHttpUrl(f.source_url);
                const busy = retryingKey === key;
                return (
                  <div key={key} className="p-3.5 sm:p-4 bg-rose-50/40 dark:bg-rose-950/20 hover:bg-rose-50/70 dark:hover:bg-rose-950/30 transition flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <input
                          type="checkbox"
                          checked={selectedKeys.has(key)}
                          onChange={() => toggleKey(key)}
                          disabled={!!retryRun}
                          aria-label={`${f.filename || f.label} kijelölése újrapróbálásra`}
                          className="w-4 h-4 accent-amber-600 shrink-0"
                        />
                        <span className="p-1.5 rounded-lg bg-rose-100 dark:bg-rose-950/70 shrink-0"><XCircle className="w-4 h-4 text-rose-600" /></span>
                        <h4 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white truncate max-w-md" title={f.filename || f.label}>
                          {f.filename || f.label || 'névtelen melléklet'}
                        </h4>
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md bg-rose-100 dark:bg-rose-950/80 text-rose-800 dark:text-rose-300 border border-rose-300 dark:border-rose-800">
                          Nem töltődött le
                        </span>
                      </div>
                      <div className="text-[11px] text-rose-700 dark:text-rose-300 pl-0 sm:pl-14">
                        Ok: {retryErrors[key] || f.reason || 'ismeretlen'}
                        <span className="text-slate-400"> · {f.attempts}. próbálkozás{f.last_attempt_at ? `, utoljára ${new Date(f.last_attempt_at.replace(' ', 'T') + 'Z').toLocaleString('hu-HU')}` : ''}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 pl-0 sm:pl-14 flex-wrap">
                        <span className="px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-cyan-300 font-semibold font-mono text-[11px] border border-blue-200 dark:border-blue-800/60">
                          {f.record_pont}
                        </span>
                        <span className="font-medium text-slate-700 dark:text-slate-300 truncate max-w-sm sm:max-w-lg" title={f.record_title}>{f.record_title}</span>
                        <span className="text-[11px] text-slate-400">· {f.page_icon} {f.page_title}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2 self-end sm:self-center shrink-0">
                      {src && (
                        <a href={src} target="_blank" rel="noopener noreferrer nofollow" className="p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition" title="Eredeti fájl megnyitása a forrásoldalon">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      {isWebpageFailure(f) && (
                        <button
                          onClick={() => setWebpageFor(f)}
                          disabled={!!retryRun}
                          className="px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer"
                          title="A weboldal tartalmának mentése a tételhez képként, PDF-ként és/vagy HTML-ként"
                        >
                          <Globe className="w-3.5 h-3.5" />
                          <span>Mentés weboldalként</span>
                        </button>
                      )}
                      <button
                        onClick={() => runRetry([key])}
                        disabled={!!retryRun}
                        className="px-2.5 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer"
                        title="Letöltés újrapróbálása a forrásoldalról"
                      >
                        <RotateCcw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} />
                        <span>Újrapróbálás</span>
                      </button>
                      <button
                        onClick={() => dismissFailure(f)}
                        disabled={!!retryRun}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                        title="Nem kell – levétel a listáról"
                        aria-label={`${f.filename || f.label} levétele a listáról`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => { onClose(); onOpenRecordAttachments(f.record_id, f.page_id); }}
                        className="px-2.5 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-950/60 hover:bg-blue-100 dark:hover:bg-blue-900/60 text-blue-700 dark:text-cyan-300 border border-blue-200 dark:border-blue-800/60 text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer"
                        title="Mellékletkezelő megnyitása – kézi feltöltéshez"
                      >
                        <FolderOpen className="w-3.5 h-3.5" />
                        <span>Mellékletek</span>
                      </button>
                      <button
                        onClick={() => { onClose(); onNavigateToRecord(f.page_id, f.record_id); }}
                        className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold shadow-xs flex items-center gap-1.5 transition cursor-pointer"
                        title="Ugrás a közzétételi ponthoz"
                      >
                        <span>Ugrás</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
              {filteredFiles.map(file => {
                const isReplacingThis = isReplacing && replacingId === file.id;

                return (
                  <div
                    key={file.id}
                    className={`p-3.5 sm:p-4 hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                      file.is_warning ? 'bg-amber-50/40 dark:bg-amber-950/20' : ''
                    }`}
                  >
                    {/* Fájl és Pont adatok */}
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {file.is_warning && safeHttpUrl(file.source_url) && (
                          <input
                            type="checkbox"
                            checked={selectedKeys.has(`a:${file.id}`)}
                            onChange={() => toggleKey(`a:${file.id}`)}
                            disabled={!!retryRun}
                            aria-label={`${file.filename} kijelölése újra letöltésre`}
                            className="w-4 h-4 accent-amber-600 shrink-0"
                          />
                        )}
                        <span className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 shrink-0">
                          {getFileIcon(file.filename)}
                        </span>
                        <h4 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white truncate max-w-md" title={file.filename}>
                          {file.filename}
                        </h4>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-semibold">
                          {formatFileSize(file.size)}
                        </span>
                        {file.is_warning && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700">
                            <AlertTriangle className="w-3 h-3 text-amber-600" />
                            HTML formátum (nem PDF)
                          </span>
                        )}
                      </div>
                      {retryErrors[`a:${file.id}`] && (
                        <div className="text-[11px] text-rose-700 dark:text-rose-300 pl-0 sm:pl-8">Újra letöltés: {retryErrors[`a:${file.id}`]}</div>
                      )}

                      {/* Melyik pontnál található */}
                      <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 pl-0 sm:pl-8 flex-wrap">
                        <span className="px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-cyan-300 font-semibold font-mono text-[11px] border border-blue-200 dark:border-blue-800/60">
                          {file.record_pont}
                        </span>
                        <span className="font-medium text-slate-700 dark:text-slate-300 truncate max-w-sm sm:max-w-lg" title={file.record_title}>
                          {file.record_title}
                        </span>
                        <span className="text-[11px] text-slate-400">
                          · {file.page_icon} {file.page_title}
                        </span>
                      </div>
                    </div>

                    {/* Akció gombok */}
                    <div className="flex items-center gap-1.5 sm:gap-2 self-end sm:self-center shrink-0">
                      {/* Előnézet */}
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                        title="Megtekintés új lapon"
                      >
                        <Eye className="w-4 h-4" />
                      </a>

                      {/* Letöltés */}
                      <a
                        href={`${file.url}?download=1`}
                        download={file.filename}
                        className="p-1.5 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                        title="Letöltés közvetlenül"
                      >
                        <Download className="w-4 h-4" />
                      </a>

                      {/* HTML-ként érkezett fájl: újra letöltés az eredeti címről */}
                      {file.is_warning && safeHttpUrl(file.source_url) && (
                        <button
                          onClick={() => runRetry([`a:${file.id}`])}
                          disabled={!!retryRun}
                          className="px-2.5 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer"
                          title="Újra letöltés az eredeti címről (pl. ha a forrásoldal akkor bejelentkező oldalt adott)"
                        >
                          <RotateCcw className={`w-3.5 h-3.5 ${retryingKey === `a:${file.id}` ? 'animate-spin' : ''}`} />
                          <span>Újra letöltés</span>
                        </button>
                      )}

                      {/* Fájl cseréje (új fájl feltöltése) */}
                      <button
                        onClick={() => handleTriggerReplace(file.id)}
                        disabled={isReplacingThis}
                        className="px-2.5 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer"
                        title="Fájl közvetlen cseréje egy új változattal"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${isReplacingThis ? 'animate-spin text-blue-500' : ''}`} />
                        <span>Csere</span>
                      </button>

                      {/* Mellékletek & Pont kezelő megnyitása */}
                      <button
                        onClick={() => {
                          onClose();
                          onOpenRecordAttachments(file.record_id, file.page_id);
                        }}
                        className="px-2.5 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-950/60 hover:bg-blue-100 dark:hover:bg-blue-900/60 text-blue-700 dark:text-cyan-300 border border-blue-200 dark:border-blue-800/60 text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer"
                        title="Mellékletkezelő megnyitása további feltöltéshez"
                      >
                        <FolderOpen className="w-3.5 h-3.5" />
                        <span>Mellékletek</span>
                      </button>

                      {/* Ugrás a ponthoz */}
                      <button
                        onClick={() => {
                          onClose();
                          onNavigateToRecord(file.page_id, file.record_id);
                        }}
                        className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold shadow-xs flex items-center gap-1.5 transition cursor-pointer"
                        title="Ugrás a közzétételi ponthoz a kártyanézetben"
                      >
                        <span>Ugrás</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
              </>
            )}
          </div>
        </div>

        {webpageFor && (
          <WebpageSaveDialog
            failure={webpageFor}
            onClose={() => setWebpageFor(null)}
            onSaved={async (msg) => {
              setWebpageFor(null);
              setSuccessToast(msg);
              setTimeout(() => setSuccessToast(null), 7000);
              await loadData();
            }}
          />
        )}

        {/* Modal Lábléc */}
        <div className="p-3.5 sm:p-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3 bg-slate-50/50 dark:bg-slate-900/50 text-xs text-slate-500 dark:text-slate-400">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
            <span>A „Csere” azonnal felülírja a tárolt állományt, az „Ugrás” pedig kinyitja az érintett pont kártyáját.</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 font-semibold transition cursor-pointer"
          >
            Bezárás
          </button>
        </div>

      </div>
    </div>
  );
};
